import { useMemo } from 'react';

import {
   AppointmentCreateCommand,
   AppointmentEncounterMode,
   AppointmentTimeSlotOccupiedFault,
} from '@dreryk/egabinet-appointment-frontend-api';
import { ReceptionServiceId } from '@dreryk/egabinet-private-medical-service-published-language-api';
import { TimeSlot } from '@dreryk/time-slot-common-api';
import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import {
   ActionEventInterceptor,
   dispatchSnackbarNegativeEvent,
   dispatchSnackbarPositiveEvent,
} from '@dreryk/react-components';
import { Choice, Slot } from '@dreryk/watermelon';

import { AppointmentAdvancePaymentShowModalEventInterceptor } from '~libs.desktop.shared.appointment-advance-payment';

import { appointmentsTranslationKeys } from '../../../../translations';
import { parseToDateWithMidnightAdjustment } from '../../shared/form/AppointmentForm.utils';
import { AppointmentUpdateFormBean, AppointmentUpdateFormController } from '../shared/AppointmentUpdateForm';

type AppointmentRestoreActionEventInterceptorProps = Readonly<
   Pick<AppointmentUpdateFormController, 'handleSubmit'> & {
      children: JSX.Element;
      appointmentId: number;
   }
>;

export const AppointmentRestoreActionEventInterceptor = (
   props: AppointmentRestoreActionEventInterceptorProps
): JSX.Element => {
   const appointmentId = useMemo(() => AppointmentId.of(props.appointmentId), [props.appointmentId]);
   const { executeAsync: restoreAppointment } = useCqrsCommand<
      void,
      AppointmentUpdateFormBean,
      AppointmentTimeSlotOccupiedFault
   >(
      (parameters) => {
         const command = new AppointmentCreateCommand();
         command.replacingAppointmentId = Slot.of(AppointmentId.of(props.appointmentId));
         command.patientId = parameters.patient!.id!;
         command.practitionerId = parameters.practitioner.id;
         command.note = Choice.withUseDefaultIfNull(parameters.note);
         command.location = parameters.healthcareProviderLocation!.locationIdentifier;

         command.timeSlot = TimeSlot.of(
            parseToDateWithMidnightAdjustment('START', parameters.date, parameters.startTime),
            parseToDateWithMidnightAdjustment('END', parameters.date, parameters.endTime)
         );

         if (parameters.videoConsultation) {
            command.encounterMode = Choice.of<AppointmentEncounterMode>('VIDEO_CONSULTATION');
         }

         if (parameters.receptionService?.value) {
            command.receptionServiceIds = parameters.receptionService.value.map(
               ({ receptionService: { receptionServiceId } }) => ReceptionServiceId.of(receptionServiceId)
            );
         }

         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(appointmentsTranslationKeys.successfullyCreatedNewAppointment);
         },
         onFault: {
            'egabinet-appointment-frontend-api:AppointmentTimeSlotOccupiedFault': () =>
               dispatchSnackbarNegativeEvent(appointmentsTranslationKeys.timeSlotTaken),
         },
      }
   );

   return (
      <ActionEventInterceptor action={props.handleSubmit(restoreAppointment)}>
         <AppointmentAdvancePaymentShowModalEventInterceptor appointmentId={appointmentId}>
            {props.children}
         </AppointmentAdvancePaymentShowModalEventInterceptor>
      </ActionEventInterceptor>
   );
};
